import { useEffect, useRef, useState } from 'react'

const STAGES = [
  {
    num: '01',
    label: 'Ingest',
    title: 'Schema and identity verified at the edge',
    desc: 'Every payment hits the edge gateway first. Schema validated, tenant authenticated, trace ID stamped. The full decision is reconstructable later, line by line.',
  },
  {
    num: '02',
    label: 'Route',
    title: 'A live network map sends each payment to the right lane',
    desc: 'A live routing graph dispatches each message to the right evaluator pool by rail, amount, geography and tenant policy. Audit-logged too.',
  },
  {
    num: '03',
    label: 'Evaluate',
    title: 'Velocity, sanctions and custom rules, all firing at once',
    desc: 'Hub-and-spoke workers run every relevant check in parallel. Hundreds of rule evaluations finish in single-digit milliseconds. No sequential bottleneck anywhere.',
  },
  {
    num: '04',
    label: 'Score',
    title: 'Rule outputs roll up into typology scores',
    desc: 'Individual rule signals get bucketed into named threat scenarios (mule rings, smurfing, account takeover), each landing with its own confidence band attached.',
  },
  {
    num: '05',
    label: 'Decide',
    title: 'Thresholds applied. Pass, flag or block in one shot',
    desc: 'Your configured thresholds collapse the scores into one of three deterministic verdicts. The verdict and its full reasoning chain are persisted before any funds settle.',
  },
]

export default function StepSection() {
  const trackRef = useRef(null)
  const [activeIdx, setActiveIdx] = useState(0)
  const [scoreCounts, setScoreCounts] = useState({ mule: 0, smurf: 0, ato: 0 })
  const lastIdx = useRef(-1)
  const scoreAnimated = useRef(false)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const update = () => {
      const r = track.getBoundingClientRect()
      const total = r.height - window.innerHeight
      const passed = -r.top
      const p = Math.max(0, Math.min(1, passed / total))
      const idx = Math.min(STAGES.length - 1, Math.floor(p * STAGES.length))
      if (idx !== lastIdx.current) {
        lastIdx.current = idx
        setActiveIdx(idx)
        if (idx === 3 && !scoreAnimated.current) {
          scoreAnimated.current = true
          const targets = { mule: 824, smurf: 641, ato: 413 }
          const start = performance.now(), dur = 1200
          const tick = (t) => {
            const pp = Math.min(1, (t - start) / dur)
            const ease = 1 - Math.pow(1 - pp, 3)
            setScoreCounts({
              mule: Math.round(targets.mule * ease),
              smurf: Math.round(targets.smurf * ease),
              ato: Math.round(targets.ato * ease),
            })
            if (pp < 1) requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        }
        if (idx !== 3) scoreAnimated.current = false
      }
    }
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    update()
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  const jumpTo = (i) => {
    const track = trackRef.current
    if (!track) return
    const r = track.getBoundingClientRect()
    const total = r.height - window.innerHeight
    const targetP = (i + 0.5) / STAGES.length
    window.scrollTo({ top: window.scrollY + r.top + targetP * total, behavior: 'smooth' })
  }

  return (
    <section className="sf-how2" id="how">
      <div ref={trackRef} className="sf-how2-track">
        <div className="sf-how2-pin">
          <div className="sf-how2-pin-inner">
            <div className="sf-how2-header sf-reveal" data-sf-reveal>
              <span className="sf-eyebrow"><span className="sf-pulse" />The path a payment takes</span>
              <h2 className="sf-section-title">Five checkpoints. <em>One verdict.</em></h2>
              <p className="sf-section-lede">
                SecureFlow sits between initiation and clearance. Every payment runs a five-checkpoint
                gauntlet: risk-scored, rule-evaluated in parallel and resolved into pass, flag or block
                before funds clear.
              </p>
            </div>

            <div className="sf-how2-stepper">
              {STAGES.map((s, i) => (
                <div key={s.label} style={{ display: 'contents' }}>
                  <div
                    className={`sf-how2-step${activeIdx === i ? ' sf-active' : ''}${i < activeIdx ? ' sf-passed' : ''}`}
                    onClick={() => jumpTo(i)}
                  >
                    <span className="sf-step-dot" />
                    <span className="sf-step-lbl">{s.num} {s.label}</span>
                  </div>
                  {i < STAGES.length - 1 && (
                    <div className={`sf-how2-connector${i < activeIdx ? ' sf-passed' : ''}`} />
                  )}
                </div>
              ))}
            </div>

            <div className="sf-how2-frame">
              <div className="sf-how2-text">
                {STAGES.map((s, i) => (
                  <div key={s.num} className={`sf-how2-stage${activeIdx === i ? ' sf-active' : ''}`}>
                    <span className="sf-stage-num">{s.num}</span>
                    <span className="sf-stage-label">{s.label}</span>
                    <h3 className="sf-stage-h3">{s.title}</h3>
                    <p className="sf-stage-p">{s.desc}</p>
                  </div>
                ))}
              </div>

              <div className="sf-how2-visual">
                <div className={`sf-vis-slide${activeIdx === 0 ? ' sf-active' : ''}`}>
                  <svg className="sf-vis-ingest" viewBox="0 0 400 320">
                    <defs>
                      <radialGradient id="sfIgGate" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#0EA5E9" />
                        <stop offset="100%" stopColor="#0369A1" />
                      </radialGradient>
                    </defs>
                    <path className="sf-stream sf-s1" d="M 20,40 L 200,160" />
                    <path className="sf-stream sf-s2" d="M 380,40 L 200,160" />
                    <path className="sf-stream sf-s3" d="M 20,280 L 200,160" />
                    <path className="sf-stream sf-s4" d="M 380,280 L 200,160" />
                    <circle className="sf-pkt sf-p1" r="4" />
                    <circle className="sf-pkt sf-p2" r="4" />
                    <circle className="sf-pkt sf-p3" r="4" />
                    <circle className="sf-pkt sf-p4" r="4" />
                    <circle className="sf-gateway-glow" cx="200" cy="160" r="50" />
                    <circle cx="200" cy="160" r="34" fill="url(#sfIgGate)" />
                    <text className="sf-gateway-lbl" x="200" y="165" textAnchor="middle">EDGE</text>
                    <text className="sf-src-lbl" x="40" y="32">UPI</text>
                    <text className="sf-src-lbl" x="360" y="32" textAnchor="end">IMPS</text>
                    <text className="sf-src-lbl" x="40" y="305">RTGS</text>
                    <text className="sf-src-lbl" x="360" y="305" textAnchor="end">SWIFT</text>
                  </svg>
                </div>

                <div className={`sf-vis-slide${activeIdx === 1 ? ' sf-active' : ''}`}>
                  <svg className="sf-vis-route" viewBox="0 0 400 320">
                    <path className="sf-trunk" d="M 20,160 L 160,160" />
                    <path className="sf-branch sf-b1" d="M 160,160 Q 220,160 240,60 L 380,60" />
                    <path className="sf-branch sf-b2" d="M 160,160 Q 220,160 240,120 L 380,120" />
                    <path className="sf-branch sf-b3" d="M 160,160 Q 220,160 240,200 L 380,200" />
                    <path className="sf-branch sf-b4" d="M 160,160 Q 220,160 240,260 L 380,260" />
                    <circle className="sf-route-hub" cx="160" cy="160" r="10" />
                    <rect className="sf-route-end" x="306" y="44" width="74" height="32" rx="6" />
                    <rect className="sf-route-end" x="306" y="104" width="74" height="32" rx="6" />
                    <rect className="sf-route-end" x="306" y="184" width="74" height="32" rx="6" />
                    <rect className="sf-route-end" x="306" y="244" width="74" height="32" rx="6" />
                    <text className="sf-route-lbl" x="343" y="64" textAnchor="middle">Velocity</text>
                    <text className="sf-route-lbl" x="343" y="124" textAnchor="middle">Sanctions</text>
                    <text className="sf-route-lbl" x="343" y="204" textAnchor="middle">Custom</text>
                    <text className="sf-route-lbl" x="343" y="264" textAnchor="middle">Geo</text>
                    <text className="sf-route-src" x="22" y="152">tx_msg</text>
                  </svg>
                </div>

                <div className={`sf-vis-slide${activeIdx === 2 ? ' sf-active' : ''}`}>
                  <div className="sf-vis-eval">
                    {['Velocity', 'Sanctions', 'Amount', 'Geo', 'Device', 'Behaviour'].map((lbl, idx) => (
                      <div key={lbl} className="sf-ev-cell" data-d={idx}>
                        <span className="sf-ev-lbl">{lbl}</span>
                        <span className="sf-ev-check">✓</span>
                        <span className="sf-ev-bar"><span className="sf-ev-fill" /></span>
                      </div>
                    ))}
                    <div className="sf-ev-tick">
                      All checks · <span className="sf-ev-time">7ms</span>
                    </div>
                  </div>
                </div>

                <div className={`sf-vis-slide${activeIdx === 3 ? ' sf-active' : ''}`}>
                  <div className="sf-vis-score">
                    <div className="sf-sc-row">
                      <div className="sf-sc-lbl">Mule ring</div>
                      <div className="sf-sc-num">{scoreCounts.mule}</div>
                      <div className="sf-sc-bar"><div className="sf-sc-fill" style={{ '--w': 0.82 }} /></div>
                    </div>
                    <div className="sf-sc-row">
                      <div className="sf-sc-lbl">Smurfing</div>
                      <div className="sf-sc-num">{scoreCounts.smurf}</div>
                      <div className="sf-sc-bar"><div className="sf-sc-fill" style={{ '--w': 0.64 }} /></div>
                    </div>
                    <div className="sf-sc-row">
                      <div className="sf-sc-lbl">Acct takeover</div>
                      <div className="sf-sc-num">{scoreCounts.ato}</div>
                      <div className="sf-sc-bar"><div className="sf-sc-fill" style={{ '--w': 0.41 }} /></div>
                    </div>
                    <div className="sf-sc-foot">Confidence bands compose into typology vector</div>
                  </div>
                </div>

                <div className={`sf-vis-slide${activeIdx === 4 ? ' sf-active' : ''}`}>
                  <svg className="sf-vis-decide" viewBox="0 0 400 320">
                    <path className="sf-dc-path" d="M 30,160 L 180,160" />
                    <circle className="sf-dc-pkt" r="6" />
                    <path className="sf-dc-diamond" d="M 180,160 L 220,120 L 260,160 L 220,200 Z" />
                    <text className="sf-dc-diamond-t" x="220" y="165" textAnchor="middle">Decide</text>
                    <rect className="sf-dc-gate sf-g-pass" x="290" y="40" width="100" height="56" rx="10" />
                    <rect className="sf-dc-gate sf-g-flag" x="290" y="132" width="100" height="56" rx="10" />
                    <rect className="sf-dc-gate sf-g-rjct" x="290" y="224" width="100" height="56" rx="10" />
                    <text className="sf-dc-gate-t" x="340" y="62" textAnchor="middle">PASS</text>
                    <text className="sf-dc-gate-s" x="340" y="80" textAnchor="middle">cleared</text>
                    <text className="sf-dc-gate-t" x="340" y="154" textAnchor="middle">FLAG</text>
                    <text className="sf-dc-gate-s" x="340" y="172" textAnchor="middle">alert</text>
                    <text className="sf-dc-gate-t" x="340" y="246" textAnchor="middle">REJECT</text>
                    <text className="sf-dc-gate-s" x="340" y="264" textAnchor="middle">blocked</text>
                    <path className="sf-dc-conn" d="M 260,160 Q 280,160 290,68" />
                    <path className="sf-dc-conn" d="M 260,160 L 290,160" />
                    <path className="sf-dc-conn" d="M 260,160 Q 280,160 290,252" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .sf-how2 { position: relative; background: var(--bg-2); }
        .sf-how2-track { position: relative; height: 240vh; }
        .sf-how2-pin {
          position: sticky; top: 0; height: 100vh;
          display: flex; align-items: flex-start; justify-content: center;
          overflow: hidden; padding: max(96px,11vh) 0 24px;
        }
        .sf-how2-pin::before {
          content: ''; position: absolute; inset: 0;
          background:
            radial-gradient(60% 40% at 50% 0%,rgba(14,165,233,0.07),transparent 70%),
            radial-gradient(50% 35% at 90% 90%,rgba(34,197,94,0.04),transparent 60%);
          pointer-events: none;
        }
        .sf-how2-pin-inner {
          width: 100%; max-width: 1240px; margin: 0 auto;
          padding: 0 var(--gutter); position: relative; z-index: 1;
        }
        .sf-how2-header { text-align: center; max-width: 760px; margin: 0 auto 20px; }
        .sf-how2-header .sf-section-title { font-size: clamp(1.5rem,3vw,2.4rem); margin-top: 14px; }
        .sf-how2-header .sf-section-lede {
          margin-top: 12px;
          font-size: clamp(0.88rem,1vw,1rem);
          max-width: 60ch;
        }

        .sf-how2-stepper {
          display: flex; align-items: center; justify-content: center;
          gap: 0; margin-bottom: 24px; flex-wrap: wrap;
        }
        .sf-how2-step {
          display: flex; align-items: center; gap: 10px;
          cursor: pointer; padding: 8px 4px;
          color: var(--text-4);
          transition: color 0.35s var(--ease);
        }
        .sf-how2-step .sf-step-dot {
          width: 14px; height: 14px; border-radius: 50%;
          background: #fff; border: 2px solid var(--line-2);
          box-shadow: 0 2px 6px rgba(15,23,42,0.08);
          transition: all 0.35s var(--ease); position: relative;
        }
        .sf-how2-step .sf-step-dot::after {
          content: ''; position: absolute; inset: -5px;
          border-radius: 50%; border: 2px solid var(--ca-500);
          opacity: 0; transition: all 0.35s var(--ease);
        }
        .sf-how2-step .sf-step-lbl {
          font-family: var(--mono); font-size: 12px;
          letter-spacing: 0.14em; font-weight: 500; text-transform: uppercase;
        }
        .sf-how2-step.sf-passed { color: var(--ca-700); }
        .sf-how2-step.sf-passed .sf-step-dot { background: var(--ca-300); border-color: var(--ca-300); }
        .sf-how2-step.sf-active { color: var(--ca-700); }
        .sf-how2-step.sf-active .sf-step-dot {
          background: var(--ca-500); border-color: var(--ca-500);
          transform: scale(1.15);
        }
        .sf-how2-step.sf-active .sf-step-dot::after { opacity: 1; }
        .sf-how2-connector {
          flex: 0 0 36px; height: 2px;
          background: var(--line-2); border-radius: 2px;
          transition: background 0.4s var(--ease);
        }
        .sf-how2-connector.sf-passed { background: var(--ca-300); }
        @media (max-width: 880px) {
          .sf-how2-step .sf-step-lbl { display: none; }
          .sf-how2-connector { flex: 0 0 18px; }
        }

        .sf-how2-frame {
          display: grid; grid-template-columns: 1fr 1.15fr;
          gap: 48px; align-items: center;
        }
        @media (max-width: 880px) {
          .sf-how2-frame { grid-template-columns: 1fr; gap: 14px; }
          .sf-how2-pin { padding: max(64px,8vh) 0 12px; }
          .sf-how2-header { margin: 0 auto 10px; }
          .sf-how2-header :global(.sf-section-lede) { display: none; }
          .sf-how2-stepper { margin-bottom: 12px; }
          .sf-how2-text { min-height: 0; }
          .sf-stage-num { display: none; }
          .sf-stage-label { margin-top: 0; font-size: 11px; padding: 4px 10px; }
          .sf-stage-h3 { margin-top: 8px; font-size: 1.1rem; line-height: 1.2; }
          .sf-stage-p {
            margin-top: 6px; font-size: 0.85rem;
            line-height: 1.5; max-width: none;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .sf-how2-visual { height: 220px; padding: 14px; }
        }
        @media (max-height: 720px) and (min-width: 881px) {
          .sf-how2-pin { padding: max(56px,7vh) 0 12px; }
          .sf-how2-header { margin: 0 auto 12px; }
          .sf-how2-header :global(.sf-section-title) { font-size: clamp(1.25rem,2.2vw,1.7rem); margin-top: 8px; }
          .sf-how2-header :global(.sf-section-lede) {
            margin-top: 6px; font-size: 0.85rem; line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .sf-how2-stepper { margin-bottom: 14px; }
          .sf-how2-text { min-height: 0; }
          .sf-stage-num { font-size: clamp(40px,5vw,60px); line-height: 0.9; }
          .sf-stage-label { margin-top: 4px; font-size: 11.5px; padding: 5px 12px; }
          .sf-stage-h3 { margin-top: 8px; font-size: clamp(1.05rem,1.7vw,1.4rem); line-height: 1.2; }
          .sf-stage-p {
            margin-top: 8px; font-size: 0.88rem; line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          .sf-how2-visual { height: clamp(220px,36vh,300px); padding: 16px; }
          .sf-how2-frame { gap: 28px; }
        }
        .sf-how2-text { position: relative; min-height: 300px; }
        .sf-how2-stage {
          position: absolute; inset: 0; opacity: 0;
          transform: translateY(28px); filter: blur(6px);
          transition: opacity 0.55s var(--ease),transform 0.55s var(--ease),filter 0.55s var(--ease);
          pointer-events: none;
        }
        .sf-how2-stage.sf-active {
          opacity: 1; transform: translateY(0); filter: blur(0);
          pointer-events: auto;
        }
        .sf-stage-num {
          display: block; font-family: var(--mono);
          font-size: clamp(60px,8vw,96px); font-weight: 600;
          letter-spacing: -0.05em; color: var(--ca-100); line-height: 0.9;
        }
        .sf-stage-label {
          display: inline-block; margin-top: 8px;
          font-family: var(--mono); font-size: 12.5px;
          letter-spacing: 0.20em; text-transform: uppercase;
          color: var(--ca-700); background: var(--ca-50);
          padding: 6px 14px; border-radius: 999px;
          border: 1px solid var(--ca-100);
        }
        .sf-stage-h3 {
          margin-top: 12px; font-size: clamp(1.35rem,2.6vw,2.05rem);
          font-weight: 600; letter-spacing: -0.025em; line-height: 1.15;
        }
        .sf-stage-p {
          margin-top: 12px; font-size: clamp(0.92rem,1.05vw,1.05rem);
          color: var(--text-2); line-height: 1.65; max-width: 46ch;
        }

        .sf-how2-visual {
          position: relative; height: clamp(280px,40vh,380px);
          background: #fff; border: 1px solid var(--line);
          border-radius: var(--radius-lg); overflow: hidden;
          box-shadow: var(--shadow);
          display: grid; place-items: center; padding: 24px;
        }
        @media (max-width: 880px) { .sf-how2-visual { height: 320px; } }
        .sf-vis-slide {
          position: absolute; inset: 0; display: grid; place-items: center;
          opacity: 0; transition: opacity 0.5s var(--ease);
          pointer-events: none;
        }
        .sf-vis-slide.sf-active { opacity: 1; pointer-events: auto; }
        .sf-how2-visual :global(svg) { width: 100%; height: 100%; }

        /* Visual 01 — Ingest */
        :global(.sf-vis-ingest .sf-stream) {
          stroke: var(--ca-300); stroke-width: 1.5; fill: none;
          stroke-dasharray: 5 7; opacity: 0;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-stream) {
          opacity: 1; animation: sfStream 1.4s linear infinite;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-s2) { animation-delay: 0.35s; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-s3) { animation-delay: 0.7s; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-s4) { animation-delay: 1.05s; }
        @keyframes sfStream { from{stroke-dashoffset:0} to{stroke-dashoffset:-36} }
        :global(.sf-vis-ingest .sf-pkt) {
          fill: var(--ca-500);
          filter: drop-shadow(0 0 6px rgba(14,165,233,0.6));
          opacity: 0;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-pkt) { opacity: 1; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-p1) { animation: sfPkt1 2.2s linear infinite; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-p2) { animation: sfPkt2 2.2s linear infinite 0.35s; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-p3) { animation: sfPkt3 2.2s linear infinite 0.7s; }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-p4) { animation: sfPkt4 2.2s linear infinite 1.05s; }
        @keyframes sfPkt1 { 0%{cx:20;cy:40} 90%{cx:200;cy:160;opacity:1} 100%{cx:200;cy:160;opacity:0} }
        @keyframes sfPkt2 { 0%{cx:380;cy:40} 90%{cx:200;cy:160;opacity:1} 100%{cx:200;cy:160;opacity:0} }
        @keyframes sfPkt3 { 0%{cx:20;cy:280} 90%{cx:200;cy:160;opacity:1} 100%{cx:200;cy:160;opacity:0} }
        @keyframes sfPkt4 { 0%{cx:380;cy:280} 90%{cx:200;cy:160;opacity:1} 100%{cx:200;cy:160;opacity:0} }
        :global(.sf-vis-ingest .sf-gateway-glow) {
          fill: rgba(14,165,233,0.18); transform-origin: 200px 160px;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-ingest .sf-gateway-glow) {
          animation: sfGatewayPulse 2.2s ease-in-out infinite;
        }
        @keyframes sfGatewayPulse {
          0%,100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.2); opacity: 0.7; }
        }
        :global(.sf-vis-ingest .sf-gateway-lbl) {
          fill: #fff; font-family: var(--mono);
          font-size: 13px; font-weight: 700; letter-spacing: 0.12em;
        }
        :global(.sf-vis-ingest .sf-src-lbl) {
          fill: var(--text-3); font-family: var(--mono);
          font-size: 10px; letter-spacing: 0.15em; font-weight: 600;
        }

        /* Visual 02 — Route */
        :global(.sf-vis-route .sf-trunk),
        :global(.sf-vis-route .sf-branch) {
          fill: none; stroke: var(--ca-500); stroke-width: 2;
          stroke-dasharray: 600; stroke-dashoffset: 600;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-trunk) { animation: sfDrawIn 0.7s var(--ease) 0.1s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-b1) { animation: sfDrawIn 0.6s var(--ease) 0.7s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-b2) { animation: sfDrawIn 0.6s var(--ease) 0.9s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-b3) { animation: sfDrawIn 0.6s var(--ease) 1.1s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-b4) { animation: sfDrawIn 0.6s var(--ease) 1.3s forwards; }
        @keyframes sfDrawIn { to { stroke-dashoffset: 0 } }
        :global(.sf-vis-route .sf-route-hub) {
          fill: var(--ca-500); transform-origin: 160px 160px;
          opacity: 0; transform: scale(0);
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-hub) {
          animation: sfPopIn 0.4s var(--ease-bounce) 0.5s forwards;
        }
        @keyframes sfPopIn { to { opacity: 1; transform: scale(1); } }
        :global(.sf-vis-route .sf-route-end) {
          fill: #fff; stroke: var(--ca-300); stroke-width: 1.5;
          opacity: 0; transform: translateX(-12px);
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-end) {
          animation: sfSlideIn 0.4s var(--ease) forwards;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-end:nth-of-type(1)) { animation-delay: 1.0s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-end:nth-of-type(2)) { animation-delay: 1.2s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-end:nth-of-type(3)) { animation-delay: 1.4s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-end:nth-of-type(4)) { animation-delay: 1.6s; }
        @keyframes sfSlideIn { to { opacity: 1; transform: translateX(0); } }
        :global(.sf-vis-route .sf-route-lbl) {
          fill: var(--ca-700); font-family: var(--mono);
          font-size: 11px; font-weight: 600; opacity: 0;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-lbl) {
          animation: sfFadeOnly 0.4s var(--ease) forwards;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-lbl:nth-of-type(1)) { animation-delay: 1.1s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-lbl:nth-of-type(2)) { animation-delay: 1.3s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-lbl:nth-of-type(3)) { animation-delay: 1.5s; }
        .sf-vis-slide.sf-active :global(.sf-vis-route .sf-route-lbl:nth-of-type(4)) { animation-delay: 1.7s; }
        @keyframes sfFadeOnly { to { opacity: 1; } }
        :global(.sf-vis-route .sf-route-src) {
          fill: var(--text-3); font-family: var(--mono);
          font-size: 11px; font-weight: 500;
        }

        /* Visual 03 — Evaluate */
        .sf-vis-eval {
          display: grid; grid-template-columns: repeat(3,1fr);
          gap: 10px; width: 100%; max-width: 380px; position: relative;
        }
        .sf-ev-cell {
          position: relative; padding: 14px 12px;
          background: var(--bg-2); border: 1px solid var(--line);
          border-radius: 10px; opacity: 0; transform: scale(0.85);
        }
        .sf-vis-slide.sf-active .sf-ev-cell { animation: sfEvIn 0.35s var(--ease) forwards; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="0"] { animation-delay: 0.10s; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="1"] { animation-delay: 0.18s; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="2"] { animation-delay: 0.26s; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="3"] { animation-delay: 0.34s; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="4"] { animation-delay: 0.42s; }
        .sf-vis-slide.sf-active .sf-ev-cell[data-d="5"] { animation-delay: 0.50s; }
        @keyframes sfEvIn { to { opacity: 1; transform: scale(1); } }
        .sf-ev-lbl {
          display: block; font-family: var(--mono);
          font-size: 10.5px; letter-spacing: 0.12em;
          color: var(--text-3); text-transform: uppercase; font-weight: 600;
        }
        .sf-ev-check {
          position: absolute; top: 8px; right: 10px;
          width: 18px; height: 18px; border-radius: 50%;
          background: var(--sf-500); color: #fff;
          display: grid; place-items: center;
          font-size: 12px; font-weight: 700;
          opacity: 0; transform: scale(0);
        }
        .sf-vis-slide.sf-active .sf-ev-check {
          animation: sfCheckPop 0.3s var(--ease-bounce) 0.85s forwards;
        }
        @keyframes sfCheckPop { to { opacity: 1; transform: scale(1); } }
        .sf-ev-bar {
          display: block; margin-top: 10px; height: 4px;
          background: var(--bg-3); border-radius: 999px; overflow: hidden;
        }
        .sf-ev-fill {
          display: block; height: 100%;
          background: linear-gradient(to right,var(--ca-500),var(--sf-500));
          border-radius: 999px;
          transform: scaleX(0); transform-origin: 0% 50%;
        }
        .sf-vis-slide.sf-active .sf-ev-fill {
          animation: sfEvFill 0.65s var(--ease) 0.5s forwards;
        }
        @keyframes sfEvFill { to { transform: scaleX(1); } }
        .sf-ev-tick {
          grid-column: 1 / -1; margin-top: 8px;
          text-align: center; font-family: var(--mono);
          font-size: 11.5px; color: var(--text-3); opacity: 0;
        }
        .sf-ev-time {
          color: var(--sf-700); font-weight: 700;
          background: var(--sf-50); padding: 1px 8px;
          border-radius: 5px; border: 1px solid var(--sf-100);
        }
        .sf-vis-slide.sf-active .sf-ev-tick {
          animation: sfFadeOnly 0.4s var(--ease) 1.1s forwards;
        }

        /* Visual 04 — Score */
        .sf-vis-score {
          width: 100%; max-width: 360px;
          display: flex; flex-direction: column; gap: 18px;
        }
        .sf-sc-row {
          display: grid; grid-template-columns: 1fr auto;
          gap: 6px 12px; align-items: center;
        }
        .sf-sc-lbl {
          grid-column: 1; font-family: var(--mono);
          font-size: 11px; letter-spacing: 0.15em;
          text-transform: uppercase; color: var(--text-3); font-weight: 600;
        }
        .sf-sc-num {
          grid-column: 2; font-family: var(--mono);
          font-size: 32px; font-weight: 600;
          letter-spacing: -0.02em; color: var(--ca-700);
          text-align: right; line-height: 1; min-width: 80px;
        }
        .sf-sc-bar {
          grid-column: 1 / -1; height: 5px;
          background: var(--bg-3); border-radius: 999px; overflow: hidden;
        }
        .sf-sc-fill {
          display: block; height: 100%;
          background: linear-gradient(to right,var(--ca-500),var(--sf-500));
          border-radius: 999px;
          transform: scaleX(0); transform-origin: 0% 50%;
          transition: transform 1.2s var(--ease);
        }
        .sf-vis-slide.sf-active .sf-sc-fill { transform: scaleX(var(--w,0.6)); }
        .sf-sc-foot {
          font-family: var(--mono); font-size: 11px;
          color: var(--text-4); letter-spacing: 0.10em;
          text-align: center; margin-top: 6px;
        }

        /* Visual 05 — Decide */
        :global(.sf-vis-decide .sf-dc-path) {
          fill: none; stroke: var(--ca-500); stroke-width: 2;
          stroke-dasharray: 170; stroke-dashoffset: 170;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-path) {
          animation: sfDrawIn 0.55s var(--ease) 0.1s forwards;
        }
        :global(.sf-vis-decide .sf-dc-pkt) {
          fill: var(--ca-500);
          filter: drop-shadow(0 0 6px rgba(14,165,233,0.6));
          opacity: 0;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-pkt) {
          opacity: 1; animation: sfDcPkt 1.5s var(--ease) 0.4s forwards;
        }
        @keyframes sfDcPkt {
          0% { cx: 30; cy: 160; }
          65% { cx: 220; cy: 160; opacity: 1; }
          100% { cx: 340; cy: 62; opacity: 0; }
        }
        :global(.sf-vis-decide .sf-dc-diamond) {
          fill: #fff; stroke: var(--ca-500); stroke-width: 2;
          opacity: 0; transform-origin: 220px 160px; transform: scale(0.6);
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-diamond) {
          animation: sfDmPop 0.45s var(--ease-bounce) 0.65s forwards;
        }
        @keyframes sfDmPop { to { opacity: 1; transform: scale(1); } }
        :global(.sf-vis-decide .sf-dc-diamond-t) {
          fill: var(--ca-700); font-family: var(--mono);
          font-size: 11px; font-weight: 600; opacity: 0;
          text-transform: uppercase; letter-spacing: 0.12em;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-diamond-t) {
          animation: sfFadeOnly 0.3s ease 0.95s forwards;
        }
        :global(.sf-vis-decide .sf-dc-conn) {
          fill: none; stroke: var(--line-2); stroke-width: 1.5;
          stroke-dasharray: 60; stroke-dashoffset: 60;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-conn:nth-of-type(1)) { animation: sfDrawIn 0.4s var(--ease) 1.0s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-conn:nth-of-type(2)) { animation: sfDrawIn 0.4s var(--ease) 1.1s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-conn:nth-of-type(3)) { animation: sfDrawIn 0.4s var(--ease) 1.2s forwards; }
        :global(.sf-vis-decide .sf-dc-gate) {
          fill: #fff; stroke: var(--line-2); stroke-width: 1.5;
          opacity: 0; transform-origin: 340px 0; transform: translateY(8px);
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-g-pass) {
          animation: sfGatePop 0.4s var(--ease) 1.25s forwards;
          stroke: var(--sf-500); stroke-width: 2; fill: var(--sf-50);
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-g-flag) { animation: sfGatePop 0.4s var(--ease) 1.4s forwards; }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-g-rjct) { animation: sfGatePop 0.4s var(--ease) 1.55s forwards; }
        @keyframes sfGatePop { to { opacity: 1; transform: translateY(0); } }
        :global(.sf-vis-decide .sf-dc-gate-t) {
          fill: var(--text); font-family: var(--mono);
          font-size: 12px; font-weight: 700; opacity: 0; letter-spacing: 0.08em;
        }
        :global(.sf-vis-decide .sf-dc-gate-s) {
          fill: var(--text-3); font-family: var(--mono);
          font-size: 9.5px; opacity: 0; letter-spacing: 0.05em;
        }
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-gate-t),
        .sf-vis-slide.sf-active :global(.sf-vis-decide .sf-dc-gate-s) {
          animation: sfFadeOnly 0.4s ease 1.5s forwards;
        }
      `}</style>
    </section>
  )
}
